import { useEffect, useState, Suspense } from "react";
import ReactDOM from "react-dom/client";
import { Route, Routes, BrowserRouter as Router, useLocation, useNavigate } from "react-router-dom";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import * as Sentry from "@sentry/react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ErrorBoundary } from "react-error-boundary";
import { CssBaseline, CircularProgress, Box } from "@mui/material";

import "./index.scss";
import "react-table-6/react-table.css";
import Header from "./components/Header.js";
import Protected from "./components/Protected.js";
import GuestOnly from "./components/GuestOnly.js";
import LegalLinks, { LEGAL_BAR_HEIGHT } from "./components/LegalLinks.js";
import ErrorFallback from "./components/ErrorFallback.js";
import Snackbar from "./components/Snackbar.js";
import ContactDialog from "./components/ContactDialog.js";
import { jwt } from "./utils/index.js";
import { openContactDialog } from "./utils/contact-feedback.js";
import theme from "./config/theme.js";
import { routes } from "./routes.js";
import "./i18n.js";

const { NODE_ENV, REACT_APP_SENTRY_DSN, REACT_APP_SENTRY_ENVIRONMENT } = process.env;

Sentry.init({
	dsn: REACT_APP_SENTRY_DSN,
	environment: REACT_APP_SENTRY_ENVIRONMENT,
	ignoreErrors: [
		"ResizeObserver loop limit exceeded",
		"ResizeObserver loop completed with undelivered notifications.",
		"Non-Error promise rejection captured",
	],
	enabled: NODE_ENV === "production",
	tracesSampleRate: 0.1,
	tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
	profilesSampleRate: 0.1,
	replaysSessionSampleRate: 0.1,
	replaysOnErrorSampleRate: 1,
});

const Loading = () => (
	<Box display="flex" justifyContent="center" alignItems="center" height="100%">
		<CircularProgress />
	</Box>
);

const App = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const [authenticated, setAuthenticated] = useState(false);

	useEffect(() => {
		setAuthenticated(jwt.isAuthenticated());
	}, [location]);

	useEffect(() => {
		const handleAuthRedirect = () => {
			jwt.destroyToken();
			navigate("/");
		};

		const handleNotFoundRedirect = () => {
			navigate("/");
		};

		window.addEventListener("ecoready:auth-redirect", handleAuthRedirect);
		window.addEventListener("ecoready:not-found-redirect", handleNotFoundRedirect);

		return () => {
			window.removeEventListener("ecoready:auth-redirect", handleAuthRedirect);
			window.removeEventListener("ecoready:not-found-redirect", handleNotFoundRedirect);
		};
	}, [navigate]);

	return (
		<StyledEngineProvider injectFirst>
			<CssBaseline />
			<ThemeProvider theme={theme}>
				<ErrorBoundary FallbackComponent={ErrorFallback}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<Header isAuthenticated={authenticated} />
						<main style={{ position: "relative", zIndex: 0, height: `calc(100vh - ${(authenticated ? 100 : 70) + LEGAL_BAR_HEIGHT}px)` }}>
							<Suspense fallback={<Loading />}>
								<Routes>
									{routes.map(({ element: Element, guestOnly, protected: isProtected, ...rest }, index) => {
										let content = <Element />;
										if (guestOnly) content = <GuestOnly c={content} />;
										else if (isProtected) content = <Protected c={content} />;
										return <Route key={index} {...rest} element={content} />;
									})}
								</Routes>
							</Suspense>
						</main>
						{/* Signed-in users report through the dialog; signed-out users get the mailto link (LegalLinks) */}
						<LegalLinks onContact={authenticated ? openContactDialog : undefined} />
						{authenticated && <ContactDialog />}
						<Snackbar />
					</LocalizationProvider>
				</ErrorBoundary>
			</ThemeProvider>
		</StyledEngineProvider>
	);
};

const root = ReactDOM.createRoot(document.querySelector("#root"));
root.render(
	<Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
		<App />
	</Router>,
);
